import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Avatar,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PortalTableCell, PortalTableHead, PortalTableRow } from '../../../styles/portalListTable';
import EventTypeBadge from './EventTypeBadge';
import EventApprovalStatusBadge from './EventApprovalStatusBadge';
import EventPublishedSwitch from './EventPublishedSwitch';
import EventRowActions from './EventRowActions';
import { isEventDatePast } from '../ManageEvent/eventTimeUtils';

const EventThumbnail = styled('img')({
  width: 48,
  height: 48,
  borderRadius: 8,
  flexShrink: 0,
  objectFit: 'cover',
  display: 'block',
  backgroundColor: '#1a1a2e',
});

const EventsListTable = ({
  events,
  onView,
  onEdit,
  onDelete,
  onPublishedChange,
  isPublishDisabled = () => false,
  showApprovalStatus = false,
  showCreatedBy = false,
  containerSx,
}) => (
  <TableContainer
    component={Paper}
    elevation={0}
    sx={{ borderRadius: 2, boxShadow: 'none', ...containerSx }}
  >
    <Table stickyHeader>
      <PortalTableHead>
        <TableRow>
          <TableCell>Event</TableCell>
          <TableCell>Type</TableCell>
          {showApprovalStatus && <TableCell>Status</TableCell>}
          <TableCell>Date &amp; Time</TableCell>
          <TableCell>Location</TableCell>
          <TableCell>Seats Available/Total</TableCell>
          {showCreatedBy && <TableCell>Created By</TableCell>}
          <TableCell align="center">Published</TableCell>
          <TableCell>Actions</TableCell>
        </TableRow>
      </PortalTableHead>
      <TableBody>
        {events.map((event) => (
          <PortalTableRow key={event.id}>
            <PortalTableCell>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {event.imageUrl ? (
                  <EventThumbnail src={event.imageUrl} alt="" loading="lazy" />
                ) : (
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1,
                      bgcolor: '#1a1a2e',
                      flexShrink: 0,
                    }}
                  />
                )}
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {event.title}
                </Typography>
              </Box>
            </PortalTableCell>
            <PortalTableCell>
              <EventTypeBadge type={event.type} />
            </PortalTableCell>
            {showApprovalStatus && (
              <PortalTableCell>
                <EventApprovalStatusBadge status={event.approvalStatus} />
              </PortalTableCell>
            )}
            <PortalTableCell>{event.dateTime}</PortalTableCell>
            <PortalTableCell>{event.location}</PortalTableCell>
            <PortalTableCell>
              {event.seatsAvailable}/{event.totalSeats}
            </PortalTableCell>
            {showCreatedBy && (
              <PortalTableCell>
                {event.createdByPartnerId && event.createdByName ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar
                      src={event.createdByAvatarUrl || undefined}
                      alt={event.createdByName}
                      sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                    >
                      {event.createdByName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {event.createdByName}
                    </Typography>
                  </Box>
                ) : (
                  '—'
                )}
              </PortalTableCell>
            )}
            <PortalTableCell align="center">
              <EventPublishedSwitch
                checked={event.published}
                disabled={isPublishDisabled(event)}
                onChange={(e) => onPublishedChange?.(event.id, e.target.checked)}
                inputProps={{ 'aria-label': `Toggle published for ${event.title}` }}
              />
            </PortalTableCell>
            <PortalTableCell>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <EventRowActions
                  eventId={event.id}
                  onView={onView}
                  onEdit={onEdit && !isEventDatePast(event.eventDate) ? onEdit : undefined}
                  onDelete={onDelete}
                />
              </Box>
            </PortalTableCell>
          </PortalTableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export default EventsListTable;
